import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { EventType } from "@azure/msal-browser";
import HomePage from "./components/home/HomePage";
import ModulesPage from "./components/modules/ModulesPage";
import NexusDashboard from "./components/dashboard/NexusDashboard";
import Login from "./components/auth/Login";
import ErrorDialog from "./components/common/ErrorDialog";
import { LicenseManager } from "ag-grid-enterprise";
import { initAlasql } from "./db/alasqlInit";

// Set AG Grid Enterprise license key (from .env) centrally
LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_LICENSE_KEY || "");

function App() {
  const [currentView, setCurrentView] = useState("welcome");
  const { accounts, instance } = useMsal();
  const [authError, setAuthError] = useState(null);

  // Allow skipping auth locally when explicitly enabled via env
  const isLocal =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");
  const bypassAuth = isLocal && process.env.REACT_APP_BYPASS_AUTH === "true";

  // Remember where the user was so we can return after login
  useEffect(() => {
    if (accounts.length === 0) {
      sessionStorage.setItem("postLoginView", currentView);
      // Do NOT show an error here; first launch should not display a dialog
    }
  }, [accounts.length, currentView]);

  // Listen for MSAL login failures and show dialog only on failure
  useEffect(() => {
    const callbackId = instance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_FAILURE) {
        const msg =
          event.error?.message || "Authentication failed. Please try again.";
        setAuthError(msg);
      }
    });
    return () => {
      if (callbackId) instance.removeEventCallback(callbackId);
    };
  }, [instance]);

  // Initialize in-memory SQL DB for dev queries
  useEffect(() => {
    try {
      initAlasql();
    } catch (e) {
      // swallow - dev tool only
      // console.warn('Failed to initialize alasql', e);
    }
  }, []);

  // Restore view after successful sign-in
  useEffect(() => {
    if (accounts.length > 0) {
      const saved = sessionStorage.getItem("postLoginView");
      if (saved) {
        setCurrentView(saved);
        sessionStorage.removeItem("postLoginView");
      }
      // Clear any prior auth error once signed in
      if (authError) setAuthError(null);
    }
  }, [accounts.length, authError]);

  const handleNavigate = (view) => {
    setCurrentView(view);
  };

  // If not authenticated, show login screen (unless bypassing locally)
  if (!bypassAuth && accounts.length === 0) {
    return (
      <>
        <Login />
        {authError && (
          <ErrorDialog
            title="Authentication required"
            message={authError}
            onClose={() => setAuthError(null)}
          />
        )}
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {currentView === "welcome" && <HomePage onNavigate={handleNavigate} />}
      {currentView === "modules" && <ModulesPage onNavigate={handleNavigate} />}
      {currentView === "discovery" && (
        <NexusDashboard onNavigate={handleNavigate} />
      )}
    </div>
  );
}

export default App;
